import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { DataGrid, } from "@mui/x-data-grid";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import WorkPreparationContracts from "../../../infrastructure/api/pages/work-preparation-contracts-api";
import { Layout } from "../../components/Layout/Layout";
import { WorkPreparationTopHeader } from "./WorkpreparationTopHeader";
import { filterFieldSetter, useSessionFilterMemory, } from "../../hooks/useSessionFilterMemory";
const DEFAULT_FILTERS = {
    search: "",
    completed: false,
    page: 0,
    pageSize: 10,
};
const revalidateFilters = (restored) => (Object.assign(Object.assign({}, restored), { search: typeof restored.search === "string" ? restored.search : "", completed: restored.completed === true, page: Number.isInteger(restored.page) && restored.page >= 0 ? restored.page : 0, 
    // The grid only offers a page size of 10; anything else stored is ignored.
    pageSize: DEFAULT_FILTERS.pageSize }));
export const Workpreparation = () => {
    const theme = useTheme();
    const { t: translate } = useTranslation();
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [filters, setFilters] = useSessionFilterMemory("work-preparation", DEFAULT_FILTERS, revalidateFilters);
    const { search, completed } = filters;
    const setSearch = filterFieldSetter(setFilters, "search", { page: 0 });
    const setCompleted = filterFieldSetter(setFilters, "completed", { page: 0 });
    // Memoised so the DataGrid isn't handed a brand-new object on every render.
    const paginationModel = useMemo(() => ({ page: filters.page, pageSize: filters.pageSize }), [filters.page, filters.pageSize]);
    useEffect(() => {
        setLoading(true);
        WorkPreparationContracts.getAllContracts(completed, search)
            .then((response) => {
            if (response.status === 200 && response.data != null) {
                // setContracts(response.data);
                setContracts(response.data);
            }
        })
            .finally(() => {
            setTimeout(() => setLoading(false), 400);
        });
    }, [search, completed]);
    // A remembered page can point past the end of a shrunken result set (contracts
    // completed in the meantime); snap back to the first page rather than render an
    // empty grid.
    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(contracts.length / filters.pageSize) - 1);
        if (filters.page > lastPage) {
            setFilters((prev) => (Object.assign(Object.assign({}, prev), { page: lastPage })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contracts, filters.page, filters.pageSize]);
    const columnsForDataGrid = [
        {
            field: "contractNumber",
            headerName: translate("ContractNumber"),
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            minWidth: 20,
        },
        {
            field: "leadNumber",
            headerName: translate("LeadNumber"),
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            minWidth: 20,
        },
        {
            field: "contractName",
            headerName: translate("ContractName"),
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            // hideSortIcons: true,
            minWidth: 150,
        },
        {
            field: "companyName",
            headerName: translate("RelationName"),
            sortable: true,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            minWidth: 150,
        },
        {
            field: "totalObjects",
            headerName: translate("TotalObjects"),
            sortable: false,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: true,
            minWidth: 30,
            //   format: (value: number) => value.toFixed(2),
        },
        {
            field: "preferredMonth",
            headerName: translate("InspectionDate"),
            sortable: false,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: true,
            minWidth: 170,
            renderCell: (params) => (_jsx(Typography, { variant: "body2", children: params.row.preferredMonth
                    ? new Date(params.row.preferredMonth).toLocaleDateString("nl")
                    : "" })),
        },
        {
            field: "isSigned",
            headerName: "Contract getekend",
            sortable: false,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: true,
            minWidth: 100,
            renderCell: (params) => params.row.isSigned ? (_jsx(CheckCircle, { size: 25, color: "green" })) : (_jsx(XCircle, { size: 25, color: "red" })),
        },
        {
            field: "isContinous",
            headerName: "Doorlopend contract",
            sortable: false,
            flex: 1,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: true,
            minWidth: 100,
            renderCell: (params) => params.row.isContinous ? (_jsx(CheckCircle, { size: 25, color: "green" })) : (_jsx(XCircle, { size: 25, color: "red" })),
        },
    ];
    const handleEvent = (params, // GridRowParams
    event, // MuiEvent<React.MouseEvent<HTMLElement>>
    details // GridCallbackDetails
    ) => {
        const contract = params.row;
        if (contract) {
            const id = contract.id;
            const orderNumber = contract.leadNumber;
            const newUrl = `/workpreparation-details?${new URLSearchParams({
                id,
                orderNumber,
            }).toString()}`;
            navigate(newUrl);
        }
    };
    return (_jsxs(Layout, { children: [_jsx(WorkPreparationTopHeader, { search: search, setSearch: setSearch, completed: completed, setCompleted: setCompleted }), _jsx(Box, { sx: {
                    marginTop: "1%",
                    padding: "16px 32px",
                    bgcolor: theme.palette.common.white,
                    borderRadius: "24px",
                }, children: _jsx(Paper, { sx: { width: "100%", overflow: "hidden", boxShadow: "none" }, children: _jsx(DataGrid, { rows: contracts !== null && contracts !== void 0 ? contracts : [], onRowClick: handleEvent, columns: columnsForDataGrid, paginationModel: paginationModel, onPaginationModelChange: (model) => setFilters((prev) => (Object.assign(Object.assign({}, prev), { page: model.page, pageSize: model.pageSize }))), pageSizeOptions: [10], disableRowSelectionOnClick: true, hideFooterPagination: !(contracts === null || contracts === void 0 ? void 0 : contracts.length), localeText: { noRowsLabel: translate("Geen rijen"), noResultsOverlayLabel: translate("Geen resultaten") }, rowHeight: 80, sx: {
                            height: {
                                xs: "300px",
                                md: "calc(100svh - 350px)",
                            },
                            border: "none",
                            fontSize: "14px",
                            "& .has-contract-row": {
                                backgroundColor: "#ffff",
                            },
                            "& .MuiDataGrid-columnHeaderTitle ": {
                                fontSize: "20px",
                                fontWeight: 400,
                            },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }, sortingOrder: ["asc", "desc"] }, "leads-grid") }) })] }));
};
